import { applyEasing } from '../../engine/animation/easing.js'
import type { PlaybackState } from '../../engine/animation/advancePlayback.js'
import {
  type ModuleShape,
  type ProjectDocumentV2,
  type RGBColor,
} from '../../project/ProjectDocument.js'
import type { RenderViewportView } from '../RenderBackend.js'

export const BUILT_IN_SHAPES = Object.freeze([
  'x', 'square', 'circle', 'diamond', 'star', 'dot', 'triangle',
  'hexagon', 'cross', 'heart', 'crescent', 'ring',
] as const)

export type BuiltInShape = (typeof BUILT_IN_SHAPES)[number]
export type FillRule = 'nonzero' | 'evenodd'

export type ShapeCommand =
  | Readonly<{ kind: 'move'; x: number; y: number }>
  | Readonly<{ kind: 'line'; x: number; y: number }>
  | Readonly<{ kind: 'cubic'; cp1x: number; cp1y: number; cp2x: number; cp2y: number; x: number; y: number }>
  | Readonly<{ kind: 'circle'; x: number; y: number; radius: number }>
  | Readonly<{
    kind: 'arc'
    x: number
    y: number
    radius: number
    startAngle: number
    endAngle: number
    counterclockwise: boolean
  }>
  | Readonly<{ kind: 'centerline'; x1: number; y1: number; x2: number; y2: number; width: number }>
  | Readonly<{ kind: 'close' }>

export interface CanonicalShape {
  readonly id: BuiltInShape
  readonly fillRule: FillRule
  readonly commands: readonly ShapeCommand[]
  readonly bounds: readonly [minimumX: number, minimumY: number, maximumX: number, maximumY: number]
}

function freezeCommands(commands: ShapeCommand[]): readonly ShapeCommand[] {
  for (let index = 0; index < commands.length; index += 1) Object.freeze(commands[index])
  return Object.freeze(commands)
}

function polygonCommands(points: ReadonlyArray<readonly [number, number]>): readonly ShapeCommand[] {
  const commands: ShapeCommand[] = [{ kind: 'move', x: points[0]?.[0] ?? 0, y: points[0]?.[1] ?? 0 }]
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index]
    if (point !== undefined) commands.push({ kind: 'line', x: point[0], y: point[1] })
  }
  commands.push({ kind: 'close' })
  return freezeCommands(commands)
}

function radialPolygon(vertexCount: number, radius: number, startAngle: number): readonly ShapeCommand[] {
  const points: Array<readonly [number, number]> = []
  for (let index = 0; index < vertexCount; index += 1) {
    const angle = startAngle + index * Math.PI * 2 / vertexCount
    points.push([Math.cos(angle) * radius, Math.sin(angle) * radius])
  }
  return polygonCommands(points)
}

function starCommands(): readonly ShapeCommand[] {
  const points: Array<readonly [number, number]> = []
  for (let index = 0; index < 10; index += 1) {
    const angle = -Math.PI / 2 + index * Math.PI / 5
    const radius = index % 2 === 0 ? 0.6 : 0.25
    points.push([Math.cos(angle) * radius, Math.sin(angle) * radius])
  }
  return polygonCommands(points)
}

const CRESCENT_OUTER_RADIUS = 0.5
const CRESCENT_CUTTER_X = 0.25
const CRESCENT_CUTTER_RADIUS = 0.375
const CRESCENT_INTERSECTION_X = 11 / 32
const CRESCENT_INTERSECTION_Y = 3 * Math.sqrt(15) / 32
const CRESCENT_OUTER_ANGLE = Math.atan2(CRESCENT_INTERSECTION_Y, CRESCENT_INTERSECTION_X)
const CRESCENT_INNER_ANGLE = Math.atan2(
  CRESCENT_INTERSECTION_Y,
  CRESCENT_INTERSECTION_X - CRESCENT_CUTTER_X,
)

function crescentCommands(): readonly ShapeCommand[] {
  return freezeCommands([
    { kind: 'move', x: CRESCENT_INTERSECTION_X, y: CRESCENT_INTERSECTION_Y },
    {
      kind: 'arc',
      x: 0,
      y: 0,
      radius: CRESCENT_OUTER_RADIUS,
      startAngle: CRESCENT_OUTER_ANGLE,
      endAngle: -CRESCENT_OUTER_ANGLE,
      counterclockwise: false,
    },
    {
      kind: 'arc',
      x: CRESCENT_CUTTER_X,
      y: 0,
      radius: CRESCENT_CUTTER_RADIUS,
      startAngle: -CRESCENT_INNER_ANGLE,
      endAngle: CRESCENT_INNER_ANGLE,
      counterclockwise: true,
    },
    { kind: 'close' },
  ])
}

function canonical(
  id: BuiltInShape,
  fillRule: FillRule,
  commands: readonly ShapeCommand[],
  bounds: readonly [number, number, number, number],
): CanonicalShape {
  return Object.freeze({ id, fillRule, commands, bounds: Object.freeze(bounds) })
}

export const CANONICAL_SHAPES: Readonly<Record<BuiltInShape, CanonicalShape>> = Object.freeze({
  x: canonical('x', 'nonzero', freezeCommands([
    { kind: 'centerline', x1: -0.5, y1: -0.5, x2: 0.5, y2: 0.5, width: 0.25 },
    { kind: 'centerline', x1: -0.5, y1: 0.5, x2: 0.5, y2: -0.5, width: 0.25 },
  ]), [-0.625, -0.625, 0.625, 0.625]),
  square: canonical('square', 'nonzero', polygonCommands([
    [-0.5, -0.5], [0.5, -0.5], [0.5, 0.5], [-0.5, 0.5],
  ]), [-0.5, -0.5, 0.5, 0.5]),
  circle: canonical('circle', 'nonzero', freezeCommands([
    { kind: 'circle', x: 0, y: 0, radius: 0.5 },
  ]), [-0.5, -0.5, 0.5, 0.5]),
  diamond: canonical('diamond', 'nonzero', polygonCommands([
    [0, -0.7], [0.7, 0], [0, 0.7], [-0.7, 0],
  ]), [-0.7, -0.7, 0.7, 0.7]),
  star: canonical('star', 'nonzero', starCommands(), [-0.571, -0.6, 0.571, 0.485]),
  dot: canonical('dot', 'nonzero', freezeCommands([
    { kind: 'circle', x: 0, y: 0, radius: 0.2 },
  ]), [-0.2, -0.2, 0.2, 0.2]),
  triangle: canonical('triangle', 'nonzero', polygonCommands([
    [0, -0.6], [0.6, 0.42], [-0.6, 0.42],
  ]), [-0.6, -0.6, 0.6, 0.42]),
  hexagon: canonical('hexagon', 'nonzero', radialPolygon(6, 0.5, -Math.PI / 2), [-0.433, -0.5, 0.433, 0.5]),
  cross: canonical('cross', 'nonzero', freezeCommands([
    { kind: 'centerline', x1: 0, y1: -0.5, x2: 0, y2: 0.5, width: 0.3 },
    { kind: 'centerline', x1: -0.5, y1: 0, x2: 0.5, y2: 0, width: 0.3 },
  ]), [-0.65, -0.65, 0.65, 0.65]),
  heart: canonical('heart', 'nonzero', freezeCommands([
    { kind: 'move', x: 0, y: 0.225 },
    { kind: 'cubic', cp1x: -0.45, cp1y: -0.135, cp2x: -0.225, cp2y: -0.45, x: 0, y: -0.135 },
    { kind: 'cubic', cp1x: 0.225, cp1y: -0.45, cp2x: 0.45, cp2y: -0.135, x: 0, y: 0.225 },
    { kind: 'close' },
  ]), [-0.45, -0.45, 0.45, 0.225]),
  crescent: canonical('crescent', 'nonzero', crescentCommands(), [-0.5, -0.5, 0.5, 0.5]),
  ring: canonical('ring', 'evenodd', freezeCommands([
    { kind: 'circle', x: 0, y: 0, radius: 0.5 },
    { kind: 'circle', x: 0, y: 0, radius: 0.3 },
  ]), [-0.5, -0.5, 0.5, 0.5]),
})

function isBuiltInShape(value: unknown): value is BuiltInShape {
  return typeof value === 'string' && (BUILT_IN_SHAPES as readonly string[]).includes(value)
}

export interface ResolvedCustomShape {
  readonly dataUrl: string
  readonly stableKey: string
  readonly viewBox: readonly [x: number, y: number, width: number, height: number]
  readonly preserveAspectRatio: 'xMidYMid meet'
}

export interface AspectFitRect {
  readonly source: readonly [x: number, y: number, width: number, height: number]
  readonly destination: readonly [x: number, y: number, width: number, height: number]
}

export function resolveCustomSvgAspectFit(
  custom: ResolvedCustomShape,
  targetWidth: number,
  targetHeight: number,
): AspectFitRect {
  if (!Number.isFinite(targetWidth) || !Number.isFinite(targetHeight) || targetWidth <= 0 || targetHeight <= 0) {
    throw new RangeError('Custom SVG fit target must be finite and positive')
  }
  const viewBox = custom.viewBox
  const scale = Math.min(targetWidth / viewBox[2], targetHeight / viewBox[3])
  const width = viewBox[2] * scale
  const height = viewBox[3] * scale
  return Object.freeze({
    source: viewBox,
    destination: Object.freeze([
      (targetWidth - width) * 0.5,
      (targetHeight - height) * 0.5,
      width,
      height,
    ] as const),
  })
}

export interface ResolvedShape {
  readonly kind: 'built-in' | 'custom-svg'
  readonly stableKey: string
  readonly builtIn: CanonicalShape
  readonly squareRadius: number
  readonly custom?: ResolvedCustomShape
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value))
}

export function resolveShape(project: Readonly<ProjectDocumentV2>): ResolvedShape {
  const runtimeShape: unknown = project.particles.shape
  const squareRadius = runtimeShape === 'square'
    ? clamp(project.appearance.borderRadius, 0, 0.5)
    : 0
  if (runtimeShape === 'svg' && project.particles.customSvg !== null) {
    const svg = project.particles.customSvg
    const viewBox = Object.freeze([svg.viewBox.x, svg.viewBox.y, svg.viewBox.width, svg.viewBox.height] as const)
    const stableKey = `custom-svg:${svg.dataUrl}\u001f${viewBox.join(',')}`
    const custom = Object.freeze({
      dataUrl: svg.dataUrl,
      stableKey,
      viewBox,
      preserveAspectRatio: 'xMidYMid meet' as const,
    })
    return Object.freeze({ kind: 'custom-svg', stableKey, builtIn: CANONICAL_SHAPES.circle, squareRadius: 0, custom })
  }
  const id = isBuiltInShape(runtimeShape) ? runtimeShape : 'circle'
  return Object.freeze({
    kind: 'built-in',
    stableKey: `built-in:${id}:${String(squareRadius)}`,
    builtIn: CANONICAL_SHAPES[id],
    squareRadius,
  })
}

export interface ResolvedFramePaint {
  readonly shape: ResolvedShape
  readonly easedProgress: number
  readonly fadeAlpha: number
  readonly fillColor: readonly [number, number, number]
  readonly borderColor: readonly [number, number, number]
  readonly glowColor: readonly [number, number, number]
  readonly backgroundColor: readonly [number, number, number]
  readonly showBackground: boolean
  readonly showBorder: boolean
  readonly borderIntensity: number
  readonly borderOnTop: boolean
  readonly borderScale: number
  readonly borderWidth: number
  readonly showGlow: boolean
  readonly glowIntensity: number
  readonly glowSoftness: number
}

function rgbTuple(color: Readonly<RGBColor>): readonly [number, number, number] {
  return Object.freeze([Math.round(color.r), Math.round(color.g), Math.round(color.b)] as const)
}

function interpolateChannel(start: number, end: number, progress: number): number {
  return Math.round(start + (end - start) * progress)
}

function interpolateColor(start: Readonly<RGBColor>, end: Readonly<RGBColor>, progress: number): readonly [number, number, number] {
  return Object.freeze([
    interpolateChannel(start.r, end.r, progress),
    interpolateChannel(start.g, end.g, progress),
    interpolateChannel(start.b, end.b, progress),
  ] as const)
}

export function resolveFramePaint(
  project: Readonly<ProjectDocumentV2>,
  playback: Readonly<PlaybackState>,
): ResolvedFramePaint {
  const easedProgress = clamp(applyEasing(
    playback.progress,
    project.animation.easingType,
    project.animation.easingAmount,
    project.animation.customEasingCurve,
  ), 0, 1)
  const start = project.appearance.colorTransitionStart
  const end = project.appearance.colorTransitionEnd
  let colorProgress: number
  if (end <= start) colorProgress = easedProgress < end ? 0 : 1
  else {
    const normalized = clamp((easedProgress - start) / (end - start), 0, 1)
    colorProgress = normalized * normalized * (3 - 2 * normalized)
  }
  return Object.freeze({
    shape: resolveShape(project),
    easedProgress,
    fadeAlpha: project.appearance.fadeInChaos ? easedProgress : 1,
    fillColor: interpolateColor(project.appearance.chaosColor, project.appearance.color, colorProgress),
    borderColor: rgbTuple(project.appearance.borderColor),
    glowColor: rgbTuple(project.appearance.glowColor),
    backgroundColor: rgbTuple(project.appearance.backgroundColor),
    showBackground: project.appearance.showBackground,
    showBorder: project.appearance.showBorder,
    borderIntensity: clamp(project.appearance.borderIntensity, 0, 1),
    borderOnTop: project.appearance.borderOnTop,
    borderScale: Math.max(0, project.appearance.borderScale),
    borderWidth: clamp(project.appearance.borderRadius, 0, 0.5),
    showGlow: project.appearance.showGlow,
    glowIntensity: clamp(project.appearance.glowIntensity, 0, 1),
    glowSoftness: Math.max(0, project.appearance.glowSoftness),
  })
}

export type RenderPassKind = 'local-composite' | 'glow-fill' | 'border'
export interface RenderPass { readonly kind: RenderPassKind }
const LOCAL_PASSES = Object.freeze([Object.freeze({ kind: 'local-composite' as const })])
const FILL_PASSES = Object.freeze([Object.freeze({ kind: 'glow-fill' as const })])
const TOP_BORDER_PASSES = Object.freeze([
  Object.freeze({ kind: 'glow-fill' as const }),
  Object.freeze({ kind: 'border' as const }),
])

export function buildRenderPasses(paint: ResolvedFramePaint): readonly RenderPass[] {
  if (!paint.showBorder) return FILL_PASSES
  return paint.borderOnTop ? TOP_BORDER_PASSES : LOCAL_PASSES
}

export interface ResolvedParticleTransform {
  readonly displaySize: number
  readonly centerX: number
  readonly centerY: number
  readonly scaleX: number
  readonly scaleY: number
  readonly rotation: number
  readonly borderWidthCss: number
}

export interface MutableParticleTransform {
  displaySize: number
  centerX: number
  centerY: number
  scaleX: number
  scaleY: number
  rotation: number
  borderWidthCss: number
}

/** Allocation-free transform writer for renderer particle loops. */
export function writeParticleTransform(
  output: MutableParticleTransform,
  x: number,
  y: number,
  size: number,
  rotation: number,
  project: Readonly<ProjectDocumentV2>,
  viewport: Readonly<RenderViewportView>,
  dpr: number,
  border: boolean,
): MutableParticleTransform {
  const displaySize = Math.max(0, size) * project.particles.scale
  const borderScale = border ? Math.max(0, project.appearance.borderScale) : 1
  const deviceScale = viewport.zoom * dpr * displaySize * borderScale
  const centerX = project.canvas.width * 0.5
  const centerY = project.canvas.height * 0.5
  output.displaySize = displaySize
  output.centerX = (centerX + (x - centerX + viewport.x) * viewport.zoom) * dpr
  output.centerY = (centerY + (y - centerY + viewport.y) * viewport.zoom) * dpr
  output.scaleX = deviceScale
  output.scaleY = deviceScale
  output.rotation = rotation
  const borderWidth = clamp(project.appearance.borderRadius, 0, 0.5)
  output.borderWidthCss = Math.max(1, Math.abs(displaySize * viewport.zoom) * borderWidth)
  return output
}

export function resolveParticleTransform(
  x: number,
  y: number,
  size: number,
  rotation: number,
  project: Readonly<ProjectDocumentV2>,
  viewport: Readonly<RenderViewportView>,
  dpr: number,
  border: boolean,
): ResolvedParticleTransform {
  return writeParticleTransform({
    displaySize: 0,
    centerX: 0,
    centerY: 0,
    scaleX: 0,
    scaleY: 0,
    rotation: 0,
    borderWidthCss: 0,
  }, x, y, size, rotation, project, viewport, dpr, border)
}

function distanceToSegment(x: number, y: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1
  const dy = y2 - y1
  const lengthSquared = dx * dx + dy * dy
  const projection = lengthSquared === 0 ? 0 : clamp(((x - x1) * dx + (y - y1) * dy) / lengthSquared, 0, 1)
  return Math.hypot(x - (x1 + dx * projection), y - (y1 + dy * projection))
}

const FLATTENED_POLYGONS = new WeakMap<CanonicalShape, Float64Array>()

function flattenedPolygon(shape: CanonicalShape): Float64Array {
  const cached = FLATTENED_POLYGONS.get(shape)
  if (cached !== undefined) return cached
  const values: number[] = []
  let currentX = 0
  let currentY = 0
  let startX = 0
  let startY = 0
  let hasStart = false
  for (const command of shape.commands) {
    if (command.kind === 'move') {
      currentX = command.x
      currentY = command.y
      startX = currentX
      startY = currentY
      hasStart = true
      values.push(currentX, currentY)
    } else if (command.kind === 'line') {
      currentX = command.x
      currentY = command.y
      values.push(currentX, currentY)
    } else if (command.kind === 'cubic') {
      const fromX = currentX
      const fromY = currentY
      for (let step = 1; step <= 24; step += 1) {
        const progress = step / 24
        const remaining = 1 - progress
        values.push(
          remaining * remaining * remaining * fromX
            + 3 * remaining * remaining * progress * command.cp1x
            + 3 * remaining * progress * progress * command.cp2x
            + progress * progress * progress * command.x,
          remaining * remaining * remaining * fromY
            + 3 * remaining * remaining * progress * command.cp1y
            + 3 * remaining * progress * progress * command.cp2y
            + progress * progress * progress * command.y,
        )
      }
      currentX = command.x
      currentY = command.y
    } else if (command.kind === 'close' && hasStart && (currentX !== startX || currentY !== startY)) {
      values.push(startX, startY)
    }
  }
  const flattened = new Float64Array(values)
  FLATTENED_POLYGONS.set(shape, flattened)
  return flattened
}

function polygonContains(shape: CanonicalShape, x: number, y: number): boolean {
  const values = flattenedPolygon(shape)
  const vertexCount = values.length / 2
  let inside = false
  for (let current = 0, previous = vertexCount - 1; current < vertexCount; previous = current, current += 1) {
    const currentX = values[current * 2] ?? 0
    const currentY = values[current * 2 + 1] ?? 0
    const previousX = values[previous * 2] ?? 0
    const previousY = values[previous * 2 + 1] ?? 0
    if ((currentY > y) !== (previousY > y)
      && x < (previousX - currentX) * (y - currentY) / (previousY - currentY) + currentX) inside = !inside
  }
  return inside
}

/** Pure containment probes used by the atlas builder and Node geometry tests. */
export function pointInCanonicalShape(shape: CanonicalShape, x: number, y: number): boolean {
  if (shape.id === 'ring') {
    const radius = Math.hypot(x, y)
    return radius <= 0.5 && radius >= 0.3
  }
  if (shape.id === 'crescent') {
    return Math.hypot(x, y) <= CRESCENT_OUTER_RADIUS
      && Math.hypot(x - CRESCENT_CUTTER_X, y) >= CRESCENT_CUTTER_RADIUS
  }
  if (shape.id === 'circle') return Math.hypot(x, y) <= 0.5
  if (shape.id === 'dot') return Math.hypot(x, y) <= 0.2
  if (shape.id === 'square') return Math.abs(x) <= 0.5 && Math.abs(y) <= 0.5
  if (shape.id === 'diamond') return Math.abs(x) + Math.abs(y) <= 0.7
  if (shape.id === 'x' || shape.id === 'cross') {
    for (const command of shape.commands) {
      if (command.kind === 'centerline'
        && distanceToSegment(x, y, command.x1, command.y1, command.x2, command.y2) <= command.width * 0.5) return true
    }
    return false
  }
  return polygonContains(shape, x, y)
}

export const VISUAL_FIXTURE_MANIFEST = Object.freeze({
  singleShapes: Object.freeze([...BUILT_IN_SHAPES, 'svg' as ModuleShape]),
  particle: Object.freeze({ size: 48, rotation: 0.37, alpha: 0.61 }),
  playbackProgress: Object.freeze([0, 0.5, 1] as const),
  borderOnTop: Object.freeze([false, true] as const),
  borderScale: Object.freeze([1, 1.5] as const),
  borderColors: Object.freeze([
    Object.freeze([255, 255, 255] as const),
    Object.freeze([7, 11, 13] as const),
  ]),
  glow: Object.freeze([
    Object.freeze({ enabled: false, intensity: 0, softness: 0 }),
    Object.freeze({ enabled: true, intensity: 0.75, softness: 24 }),
  ]),
  crescentProbes: Object.freeze([
    Object.freeze({ x: -0.25, y: 0, inside: true }),
    Object.freeze({ x: 0.25, y: 0, inside: false }),
  ]),
  backgrounds: Object.freeze(['transparent', 'solid'] as const),
  viewport: Object.freeze({ x: 13, y: -9, zoom: 1.25, dpr: 2 }),
})

export const VISUAL_TOLERANCE = Object.freeze({
  minimumPixelsWithinChannelThreshold: 0.995,
  channelThreshold: 8,
  maximumChannelDifference: 32,
  minimumCoverageIoU: 0.995,
  maximumCentroidOrBoundsErrorPixels: 1,
  maximumHoleCoreAlpha: 2,
})
